import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Page, PageHeader } from "../components/PageHeader";
import { api, extractError } from "../lib/api";
import { fmtCLP, fmtDate, todayISO } from "../lib/format";
import { toast } from "sonner";
import { Send, Receipt, CheckSquare, Square, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const docKey = (tipo, id) => `${tipo}:${id}`;

function matchText(doc, fields, q) {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  return fields.some((f) => String(doc[f] ?? "").toLowerCase().includes(needle));
}

function inDateRange(fecha, desde, hasta) {
  if (!fecha) return true;
  if (desde && fecha < desde) return false;
  if (hasta && fecha > hasta) return false;
  return true;
}

export default function PagosProveedores() {
  const [facturas, setFacturas] = useState([]);
  const [boletas, setBoletas] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState({});
  const [open, setOpen] = useState(false);
  const [filtroTexto, setFiltroTexto] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [tipoDoc, setTipoDoc] = useState("todos");
  const [proveedorId, setProveedorId] = useState("");
  const [form, setForm] = useState({
    cuenta_bancaria_id: "",
    fecha: todayISO(),
    medio_pago: "transferencia",
    n_doc: "",
    glosa: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [a, b, c] = await Promise.all([
        api.get("/pagos/facturas-pendientes"),
        api.get("/pagos"),
        api.get("/cuentas-bancarias"),
      ]);
      setFacturas(a.data.facturas || []);
      setBoletas(a.data.boletas_honorarios || []);
      setPagos(b.data.pagos || []);
      setCuentas(c.data || []);
      if (c.data?.[0] && !form.cuenta_bancaria_id) setForm((f) => ({ ...f, cuenta_bancaria_id: c.data[0].id }));
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const proveedores = useMemo(() => {
    const map = new Map();
    facturas.forEach((f) => {
      if (f.contacto_id) {
        map.set(f.contacto_id, {
          id: f.contacto_id,
          nombre: f.contacto_nombre,
          rut: f.contacto_rut,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
  }, [facturas]);

  const filteredFacturas = useMemo(() => {
    if (tipoDoc === "boletas") return [];
    return facturas.filter((f) => {
      if (proveedorId && f.contacto_id !== proveedorId) return false;
      if (!inDateRange(f.fecha_emision, desde, hasta)) return false;
      return matchText(f, ["contacto_nombre", "contacto_rut", "folio"], filtroTexto);
    });
  }, [facturas, tipoDoc, proveedorId, desde, hasta, filtroTexto]);

  const filteredBoletas = useMemo(() => {
    if (tipoDoc === "facturas") return [];
    return boletas.filter((b) => {
      if (!inDateRange(b.fecha_emision, desde, hasta)) return false;
      return matchText(b, ["prestador_nombre", "prestador_rut", "folio"], filtroTexto);
    });
  }, [boletas, tipoDoc, desde, hasta, filtroTexto]);

  const selectDocs = useCallback((tipoPrefix, docs) => {
    setSel((s) => {
      const next = { ...s };
      docs.forEach((d) => {
        next[docKey(tipoPrefix, d.id)] = d.saldo_pendiente;
      });
      return next;
    });
  }, []);

  const deselectDocs = useCallback((tipoPrefix, docs) => {
    setSel((s) => {
      const next = { ...s };
      docs.forEach((d) => {
        delete next[docKey(tipoPrefix, d.id)];
      });
      return next;
    });
  }, []);

  const selectAllVisible = useCallback(() => {
    selectDocs("f", filteredFacturas);
    selectDocs("b", filteredBoletas);
  }, [filteredFacturas, filteredBoletas, selectDocs]);

  const deselectAll = useCallback(() => setSel({}), []);

  const clearFilters = () => {
    setFiltroTexto("");
    setDesde("");
    setHasta("");
    setTipoDoc("todos");
    setProveedorId("");
  };

  const hasFilters = Boolean(filtroTexto || desde || hasta || tipoDoc !== "todos" || proveedorId);

  const toggle = (tipo, doc) => {
    const key = docKey(tipo, doc.id);
    setSel((s) => {
      if (s[key] !== undefined) {
        const { [key]: _, ...rest } = s;
        return rest;
      }
      return { ...s, [key]: doc.saldo_pendiente };
    });
  };

  const toggleAllFacturas = () => {
    const keys = filteredFacturas.map((f) => docKey("f", f.id));
    const allOn = keys.length > 0 && keys.every((k) => sel[k] !== undefined);
    if (allOn) deselectDocs("f", filteredFacturas);
    else selectDocs("f", filteredFacturas);
  };

  const toggleAllBoletas = () => {
    const keys = filteredBoletas.map((b) => docKey("b", b.id));
    const allOn = keys.length > 0 && keys.every((k) => sel[k] !== undefined);
    if (allOn) deselectDocs("b", filteredBoletas);
    else selectDocs("b", filteredBoletas);
  };

  const updateMonto = (tipo, doc, val) => {
    const key = docKey(tipo, doc.id);
    const m = parseFloat(val) || 0;
    setSel((s) => ({ ...s, [key]: m }));
  };

  const items = useMemo(() => {
    const arr = [];
    Object.entries(sel).forEach(([k, monto]) => {
      const [tipo, id] = k.split(":");
      if (monto > 0) arr.push({ tipo: tipo === "f" ? "factura_compra" : "boleta_honorarios", doc_id: id, monto });
    });
    return arr;
  }, [sel]);

  const total = items.reduce((s, it) => s + it.monto, 0);
  const visibleCount = filteredFacturas.length + filteredBoletas.length;

  const allFacturasSelected = filteredFacturas.length > 0
    && filteredFacturas.every((f) => sel[docKey("f", f.id)] !== undefined);
  const someFacturasSelected = filteredFacturas.some((f) => sel[docKey("f", f.id)] !== undefined);

  const allBoletasSelected = filteredBoletas.length > 0
    && filteredBoletas.every((b) => sel[docKey("b", b.id)] !== undefined);
  const someBoletasSelected = filteredBoletas.some((b) => sel[docKey("b", b.id)] !== undefined);

  const allVisibleSelected = visibleCount > 0
    && filteredFacturas.every((f) => sel[docKey("f", f.id)] !== undefined)
    && filteredBoletas.every((b) => sel[docKey("b", b.id)] !== undefined);

  const payAllVisible = () => {
    if (visibleCount === 0) {
      toast.warning("No hay documentos pendientes con los filtros actuales");
      return;
    }
    selectAllVisible();
    setOpen(true);
  };

  const submit = async () => {
    if (items.length === 0) { toast.warning("Selecciona al menos un documento"); return; }
    if (!form.cuenta_bancaria_id) { toast.warning("Selecciona cuenta bancaria"); return; }
    try {
      await api.post("/pagos", { ...form, items });
      toast.success(`Pago registrado · ${items.length} doc(s) · ${fmtCLP(total)}`);
      setOpen(false); setSel({});
      load();
    } catch (err) { toast.error(extractError(err)); }
  };

  return (
    <Page>
      <PageHeader
        title="Pagos a proveedores"
        subtitle="Liquida facturas de compra y boletas de honorarios"
        testid="pp-header"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={selectAllVisible}
              disabled={visibleCount === 0}
              data-testid="pp-select-all"
              className="inline-flex items-center gap-1.5 border border-border hover:bg-muted disabled:opacity-50 text-sm px-3 py-2 rounded-sm"
            >
              <CheckSquare className="h-4 w-4" />
              Seleccionar todo{hasFilters ? " (filtro)" : ""}
            </button>
            <button
              type="button"
              onClick={payAllVisible}
              disabled={visibleCount === 0}
              data-testid="pp-pay-all"
              className="inline-flex items-center gap-1.5 border border-primary text-primary hover:bg-primary/10 disabled:opacity-50 text-sm px-3 py-2 rounded-sm"
            >
              <Send className="h-4 w-4" />
              Pagar todo{hasFilters ? " visible" : ""}
            </button>
            <button
              type="button"
              onClick={() => setOpen(true)}
              disabled={items.length === 0}
              data-testid="pp-pay-button"
              className="inline-flex items-center gap-1.5 bg-primary hover:bg-success disabled:opacity-50 text-primary-foreground text-sm font-medium px-3 py-2 rounded-sm"
            >
              <Send className="h-4 w-4" />
              Pagar seleccionados ({items.length})
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KPI label="Facturas pendientes" value={hasFilters ? `${filteredFacturas.length} / ${facturas.length}` : facturas.length} />
        <KPI label="Boletas pendientes" value={hasFilters ? `${filteredBoletas.length} / ${boletas.length}` : boletas.length} />
        <KPI label="Seleccionados" value={items.length} tone="primary" />
        <KPI label="Total a pagar" value={fmtCLP(total)} tone="primary" />
      </div>

      <div className="bg-card border border-border rounded-md p-3 mb-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <label className="label-caps block mb-1">Buscar</label>
            <input
              type="search"
              value={filtroTexto}
              onChange={(e) => setFiltroTexto(e.target.value)}
              placeholder="Folio, proveedor, RUT…"
              data-testid="pp-filtro-texto"
              className="w-full px-3 py-2 border border-border rounded-sm text-sm"
            />
          </div>
          <div>
            <label className="label-caps block mb-1">Desde</label>
            <input
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              data-testid="pp-filtro-desde"
              className="px-3 py-2 border border-border rounded-sm text-sm"
            />
          </div>
          <div>
            <label className="label-caps block mb-1">Hasta</label>
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              data-testid="pp-filtro-hasta"
              className="px-3 py-2 border border-border rounded-sm text-sm"
            />
          </div>
          <div className="min-w-[140px]">
            <label className="label-caps block mb-1">Tipo</label>
            <Select value={tipoDoc} onValueChange={setTipoDoc}>
              <SelectTrigger data-testid="pp-filtro-tipo"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="facturas">Solo facturas</SelectItem>
                <SelectItem value="boletas">Solo boletas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {proveedores.length > 1 && tipoDoc !== "boletas" && (
            <div className="min-w-[180px]">
              <label className="label-caps block mb-1">Proveedor</label>
              <Select value={proveedorId || "__all__"} onValueChange={(v) => setProveedorId(v === "__all__" ? "" : v)}>
                <SelectTrigger data-testid="pp-filtro-proveedor"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos los proveedores</SelectItem>
                  {proveedores.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {hasFilters && (
            <button
              type="button"
              onClick={clearFilters}
              data-testid="pp-limpiar-filtros"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground px-2 py-2"
            >
              <X className="h-4 w-4" /> Limpiar filtros
            </button>
          )}
        </div>
        {visibleCount > 0 && (
          <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-border">
            <span className="text-xs text-muted-foreground">
              {visibleCount} documento(s) visible(s)
              {items.length > 0 && ` · ${items.length} seleccionado(s)`}
            </span>
            <button
              type="button"
              onClick={allVisibleSelected ? () => { deselectDocs("f", filteredFacturas); deselectDocs("b", filteredBoletas); } : selectAllVisible}
              className="text-xs text-primary hover:underline"
            >
              {allVisibleSelected ? "Deseleccionar visibles" : "Seleccionar visibles"}
            </button>
            {items.length > 0 && (
              <button type="button" onClick={deselectAll} className="text-xs text-muted-foreground hover:underline inline-flex items-center gap-1">
                <Square className="h-3 w-3" /> Deseleccionar todo
              </button>
            )}
          </div>
        )}
      </div>

      {(tipoDoc === "todos" || tipoDoc === "facturas") && (
        <>
          <h3 className="font-display text-lg mb-2 mt-2">Facturas de compra pendientes</h3>
          <div className="bg-card border border-border rounded-md overflow-x-auto mb-6">
            {loading ? <div className="p-8 text-center text-sm text-muted-foreground">Cargando…</div>
              : facturas.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">Sin facturas pendientes ✓</div>
              : filteredFacturas.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">Ninguna factura coincide con los filtros</div>
              : (
              <table className="acc-table" data-testid="pp-facturas">
                <thead><tr>
                  <th style={{ width: 30 }}>
                    <input
                      type="checkbox"
                      checked={allFacturasSelected}
                      ref={(el) => { if (el) el.indeterminate = someFacturasSelected && !allFacturasSelected; }}
                      onChange={toggleAllFacturas}
                      data-testid="pp-check-all-facturas"
                      title="Seleccionar todas las facturas visibles"
                    />
                  </th>
                  <th>Fecha</th><th>Folio</th><th>Proveedor</th>
                  <th className="num">Total</th><th className="num">Pagado</th><th className="num">Saldo</th><th className="num">Monto a pagar</th>
                </tr></thead>
                <tbody>
                  {filteredFacturas.map((f) => {
                    const k = docKey("f", f.id);
                    const checked = sel[k] !== undefined;
                    return (
                      <tr key={f.id} className={checked ? "bg-primary/5" : ""}>
                        <td><input type="checkbox" checked={checked} onChange={() => toggle("f", f)} data-testid={`pp-check-f-${f.id}`} /></td>
                        <td>{fmtDate(f.fecha_emision)}</td>
                        <td className="font-mono text-[12px]">#{f.folio}</td>
                        <td>{f.contacto_nombre}</td>
                        <td className="num">{fmtCLP(f.total)}</td>
                        <td className="num text-[12px] text-muted-foreground">{fmtCLP(f.pagado_monto || 0)}</td>
                        <td className="num font-semibold">{fmtCLP(f.saldo_pendiente)}</td>
                        <td className="num">
                          {checked && (
                            <input type="number" value={sel[k]} onChange={(e) => updateMonto("f", f, e.target.value)}
                              className="w-28 px-2 py-1 border border-border rounded-sm text-sm text-right font-mono" />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {(tipoDoc === "todos" || tipoDoc === "boletas") && boletas.length > 0 && (
        <>
          <h3 className="font-display text-lg mb-2 mt-2">Boletas de honorarios pendientes</h3>
          <div className="bg-card border border-border rounded-md overflow-x-auto mb-6">
            {filteredBoletas.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">Ninguna boleta coincide con los filtros</div>
            ) : (
              <table className="acc-table" data-testid="pp-boletas">
                <thead><tr>
                  <th style={{ width: 30 }}>
                    <input
                      type="checkbox"
                      checked={allBoletasSelected}
                      ref={(el) => { if (el) el.indeterminate = someBoletasSelected && !allBoletasSelected; }}
                      onChange={toggleAllBoletas}
                      data-testid="pp-check-all-boletas"
                      title="Seleccionar todas las boletas visibles"
                    />
                  </th>
                  <th>Fecha</th><th>Folio</th><th>Prestador</th>
                  <th className="num">Líquido</th><th className="num">Pagado</th><th className="num">Saldo</th><th className="num">Monto a pagar</th>
                </tr></thead>
                <tbody>
                  {filteredBoletas.map((b) => {
                    const k = docKey("b", b.id);
                    const checked = sel[k] !== undefined;
                    return (
                      <tr key={b.id} className={checked ? "bg-primary/5" : ""}>
                        <td><input type="checkbox" checked={checked} onChange={() => toggle("b", b)} data-testid={`pp-check-b-${b.id}`} /></td>
                        <td>{fmtDate(b.fecha_emision)}</td>
                        <td className="font-mono text-[12px]">#{b.folio}</td>
                        <td>{b.prestador_nombre}</td>
                        <td className="num">{fmtCLP(b.monto_liquido)}</td>
                        <td className="num text-[12px] text-muted-foreground">{fmtCLP(b.pagado_monto || 0)}</td>
                        <td className="num font-semibold">{fmtCLP(b.saldo_pendiente)}</td>
                        <td className="num">
                          {checked && (
                            <input type="number" value={sel[k]} onChange={(e) => updateMonto("b", b, e.target.value)}
                              className="w-28 px-2 py-1 border border-border rounded-sm text-sm text-right font-mono" />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      <h3 className="font-display text-lg mb-2 mt-2">Historial de pagos</h3>
      <div className="bg-card border border-border rounded-md overflow-x-auto">
        {pagos.length === 0 ? <div className="p-6 text-center text-sm text-muted-foreground">Sin pagos registrados aún</div> : (
          <table className="acc-table" data-testid="pp-historial">
            <thead><tr>
              <th>Fecha</th><th>Asiento</th><th>Medio</th><th>N° doc</th><th>Docs</th><th>Glosa</th><th className="num">Monto</th>
            </tr></thead>
            <tbody>
              {pagos.map((p) => (
                <tr key={p.id}>
                  <td>{fmtDate(p.fecha)}</td>
                  <td className="font-mono text-[11px]"><Receipt className="inline h-3 w-3 mr-1" />#{p.asiento_numero}</td>
                  <td className="capitalize text-[12px]">{p.medio_pago}</td>
                  <td className="font-mono text-[11px]">{p.n_doc || "—"}</td>
                  <td className="text-[12px]">{(p.items || []).length} doc(s)</td>
                  <td className="text-[12px]">{p.glosa}</td>
                  <td className="num font-semibold">{fmtCLP(p.monto_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar pago</DialogTitle>
            <DialogDescription>Se generará un asiento contable con DEBE en cada documento pagado y HABER en la cuenta bancaria.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-caps block mb-1">Cuenta bancaria</label>
                <Select value={form.cuenta_bancaria_id} onValueChange={(v) => setForm({ ...form, cuenta_bancaria_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {cuentas.map((c) => <SelectItem key={c.id} value={c.id}>{c.banco} · {c.numero}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label-caps block mb-1">Fecha</label>
                <input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-caps block mb-1">Medio</label>
                <Select value={form.medio_pago} onValueChange={(v) => setForm({ ...form, medio_pago: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="label-caps block mb-1">N° doc / cheque</label>
                <input value={form.n_doc} onChange={(e) => setForm({ ...form, n_doc: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-sm text-sm font-mono" />
              </div>
            </div>
            <div>
              <label className="label-caps block mb-1">Glosa</label>
              <input value={form.glosa} onChange={(e) => setForm({ ...form, glosa: e.target.value })}
                placeholder="(opcional) — se genera automáticamente si vacío"
                className="w-full px-3 py-2 border border-border rounded-sm text-sm" />
            </div>
            <div className="bg-muted rounded-sm p-3 text-sm flex justify-between">
              <span><strong>{items.length}</strong> documento(s) a pagar</span>
              <span className="font-semibold font-mono">{fmtCLP(total)}</span>
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setOpen(false)} className="border border-border hover:bg-muted text-sm px-4 py-2 rounded-sm">Cancelar</button>
            <button type="button" onClick={submit} data-testid="pp-confirm"
              className="bg-primary hover:bg-success text-primary-foreground text-sm font-medium px-4 py-2 rounded-sm">
              Registrar pago
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}

function KPI({ label, value, tone = "default" }) {
  const tones = { default: "text-foreground", primary: "text-primary" };
  return (
    <div className="bg-card border border-border rounded-md p-3 sm:p-4">
      <div className="label-caps mb-1">{label}</div>
      <div className={`num text-lg font-semibold ${tones[tone]}`}>{value}</div>
    </div>
  );
}
